import './index.scss'
import { observer } from 'mobx-react-lite'
import XImg from '@assets/svg/x.svg'
import { Context } from '@/index'
import { useContext, useState, useEffect } from 'react'
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { createCreditCard as createCreditCardApi } from '@api/user-api';
import { toast } from 'react-toastify';
import { getCardType, formatExpirationDate, formatCardHolderName } from '@utils/utils';
import { toastOptions } from '@shared/index';

const validationSchema = Yup.object().shape({
    cardNumber: Yup.string()
      .required('Kart nömrəsini daxil edin')
      .min(19, 'Kart nömrəsi 16 rəqəmdən ibarət olmalıdır')
      .max(19, 'Kart nömrəsi 16 rəqəmdən ibarət olmalıdır'),
    cvc: Yup.string()
      .required('CVC/CVV daxil edin')
      .min(3, 'CVC/CVV 3-4 rəqəmdən ibarət olmalıdır')
      .max(4, 'CVC/CVV 3-4 rəqəmdən ibarət olmalıdır'),
    cardHolder: Yup.string()
      .required('Kart sahibinin adını daxil edin')
      .min(3, 'Kart sahibinin adı 3-22 simvoldan ibarət olmalıdır')
      .max(22, 'Kart sahibinin adı 3-22 simvoldan ibarət olmalıdır'),
    expirationDate: Yup.string()
      .required('Kartın etibarlılıq müddətini daxil edin')
      .typeError('Kartın etibarlılıq müddəti səhvdir. Məsələn: 05/30')
      .max(5, 'Kartın etibarlılıq müddəti səhvdir. Məsələn: 05/30')
      .matches(
        /([0-9]{2})\/([0-9]{2})/,
        'Kartın etibarlılıq müddəti səhvdir. Məsələn: 05/30'
      )
      .test(
        'test-credit-card-expiration-date',
        'Kartın etibarlılıq müddəti bitmişdir',
        expirationDate => {
          if (!expirationDate) {
            return false
          }
    
          const today = new Date()
          const monthToday = today.getMonth() + 1
          const yearToday = today
            .getFullYear()
            .toString()
            .substr(-2)
    
          const [expMonth, expYear] = expirationDate.split('/')
    
          if (Number(expYear) < Number(yearToday)) {
            return false
          } else if (
            Number(expMonth) < monthToday &&
            Number(expYear) <= Number(yearToday)
          ) {
            return false
          }
    
          return true
        }
      )
      .test(
        'test-credit-card-expiration-date',
        'Kartın etibarlılıq müddətinin ayı səhvdir',
        expirationDate => {
          if (!expirationDate) {
            return false
          }
    
          const [expMonth] = expirationDate.split('/')
    
          if (Number(expMonth) > 12) {
            return false
          }
    
          return true
        }
      )
      .required('Kartın etibarlılıq müddətini daxil edin')
});

const AddCardModal = observer(({callback = null}) => {
    const { modal } = useContext(Context);

    const [ cardType, setCardType ] = useState('');
    const [ isDisabled, setIsDisabled ] = useState(true);
    
    const formOptions = { resolver: yupResolver(validationSchema) }
    const { register, watch, handleSubmit, formState: { errors } } = useForm(formOptions);

    const onSubmit = (data) => {
        console.log(data);
        var expData = data.expirationDate.split("/");
        const payload = {
            card_number: data.cardNumber.replaceAll(" ", ""),
            exp_month: expData[0],
            exp_year: expData[1],
            cvc: data.cvc,
            cardholder_name: data.cardHolder.toUpperCase(),
            type: cardType
        };

        createCreditCardApi(payload)
        .then((res) => {
            console.log(res);
            toast.success('Kart əlavə edildi', toastOptions);
            if (callback) {
                callback();
            }
            modal.closeModal();
        })
        .catch((error) => {
            toast.error('Kart əlavə edilməsində xəta', toastOptions);
            console.log(error);
            modal.closeModal();
        });
    }

    const formatCardNumberAndCardType = (event) => {
        setCardType(getCardType(event.target.value.replace(/\s/g, '')));
        event.target.value = [...event.target.value.replace(/\s/g, '')].map((d, i) =>
            (i) % 4 === 0 ? ' ' + d : d).join('').trim()
    }

    useEffect(() => {
        const subscription = watch((value) => {
            const { cardHolder, cardNumber, cvc, expirationDate } = value;
            (cardHolder && cardNumber && cvc && expirationDate) ? setIsDisabled(false) : setIsDisabled(true);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    return (
        <div className="add-card-modal">
            <div className="header-modal">
Ödəniş üsulunu əlavə et
                <div className="x-block" onClick={() => modal.closeModal()}>
                    <img src={XImg} className="x" alt="close"/>
                </div>
            </div>
            <form className="form" onSubmit={handleSubmit(onSubmit)}>
                <div className="form-control-block">
                    <label htmlFor="cardNumber" className="form-label">
Kart nömrəsi
                    </label>
                    <input
                        maxLength="19"
                        className={`form-control ${cardType ? cardType : 'card'}`}
                        id="cardNumber"
                        placeholder="" 
                        {...register('cardNumber')}
                        onKeyUp={formatCardNumberAndCardType}
                        // onChange={(e) => {
                            
                        // }}
                    />
                    {errors.cardNumber?.message && (
                        <p className="error">{errors.cardNumber?.message}</p>
                    )}
                </div>
                <div className="form-control-block">
                <div className="row">
                    <div className="col-6">
                        <label htmlFor="expirationDate" className="form-label">
Etibarlılıq müddəti
                        </label>
                        <input
                            maxLength="5"
                            className="form-control date"
                            id="expirationDate"
                            placeholder="05/30" 
                            {...register('expirationDate')}
                            onKeyUp={formatExpirationDate}
                        />
                        {errors.expirationDate?.message && (
                            <p className="error">{errors.expirationDate?.message}</p>
                        )}
                    </div>
                    <div className="col-6">
                        <label htmlFor="cvc" className="form-label">
CVC/CVV
                        </label>
                        <input
                            maxLength="4"
                            type="password"
                            className="form-control secret"
                            id="cvc"
                            placeholder="•••"
                            {...register('cvc')}
                        />
                        {errors.cvc?.message && (
                            <p className="error">{errors.cvc?.message}</p>
                        )}
                    </div>
                </div>

                </div>
                <div className="form-control-block">
                    <label htmlFor="cardHolder" className="form-label">
Kart sahibinin adı
                    </label>
                    <input
                        className="form-control"
                        id="cardHolder"
                        placeholder="SERGEY IVANOV" 
                        {...register('cardHolder')}
                        onKeyUp={formatCardHolderName}
                    />
                    {errors.cardHolder?.message && (
                        <p className="error">{errors.cardHolder?.message}</p>
                    )}
                </div>
                <div className="actions">
                    <button type='button' className="cancel" onClick={() => modal.closeModal()}>
Bağla
                    </button>
                    <button type='submit' className="confirm" disabled={isDisabled}>
Kart əlavə et
                    </button>
                </div>
            </form>
        </div>
    )
})

export { AddCardModal }